/**
 * shuttleSchedules.js — 셔틀버스 시간대별 정차장 구조
 *
 * ROUTE_STOP_MAP: routeId → 정차장 구조 매핑
 *   boardingStopId  — 탑승 정류장 (위치 찾기 버튼의 타겟)
 *   alightingStopId — 하차 정류장
 *   stopIds         — 전체 경유 정류장 순서
 *
 * SHUTTLE_SCHEDULES: 시간대별 전체 스케줄
 *   단일 원본인 shuttleData.js(routes[].stops[0].times/fare)에서 파생 생성한다.
 *   시간/요금을 여기서 직접 하드코딩하지 않음 — shuttleData.js를 고치면 자동 반영됨.
 */

import { shuttleData } from '../shuttleData.js'
import { getTripStopIds } from './model.js'

export const ROUTE_STOP_MAP = {
  songnae_to_school: {
    stopIds: ['songnae-station', 'campus-sh'],
    boardingStopId: 'songnae-station',
    alightingStopId: 'campus-sh',
    displayStops: '송내역 → 학생회관 사거리',
  },
  school_to_songnae: {
    stopIds: ['campus-songnae-hagyo', 'songnae-station'],
    boardingStopId: 'campus-songnae-hagyo',
    alightingStopId: 'songnae-station',
    displayStops: '송내역 하교 → 송내역',
  },
  gosaek_to_school: {
    stopIds: ['gosaek-station', 'campus-inmundae', 'campus-sh'],
    boardingStopId: 'gosaek-station',
    alightingStopId: 'campus-sh',
    displayStops: '고색역 → 학생회관 사거리',
  },
  gangnam_to_school: {
    stopIds: ['gangnam-station', 'campus-sh'],
    boardingStopId: 'gangnam-station',
    alightingStopId: 'campus-sh',
    displayStops: '강남역 → 학생회관 사거리',
  },
  sadang_to_school: {
    stopIds: ['sadang-station', 'campus-sh'],
    boardingStopId: 'sadang-station',
    alightingStopId: 'campus-sh',
    displayStops: '사당역 → 학생회관 사거리',
  },
  school_to_sadang: {
    stopIds: ['campus-inmundae', 'sadang-station'],
    boardingStopId: 'campus-inmundae',
    alightingStopId: 'sadang-station',
    displayStops: '인문대 승차장 → 사당역',
  },
  byeongjeom_to_school: {
    stopIds: ['byeongjeom-station', 'campus-sh'],
    boardingStopId: 'byeongjeom-station',
    alightingStopId: 'campus-sh',
    displayStops: '병점역 → 학생회관 사거리',
  },
  school_to_byeongjeom: {
    stopIds: ['campus-byeongjeom-hagyo', 'byeongjeom-station'],
    boardingStopId: 'campus-byeongjeom-hagyo',
    alightingStopId: 'byeongjeom-station',
    displayStops: '병점역 하교 셔틀 승차장 → 병점역',
  },
  campus_loop: {
    stopIds: ['campus-inmundae'],
    boardingStopId: 'campus-inmundae',
    alightingStopId: 'campus-inmundae',
    displayStops: '교내 순환 (인문대 앞 출발)',
  },
}

// shuttleData.js(단일 원본)의 routes[].stops[0].times를 펼쳐 시간대별 스케줄로 변환한다.
// 시간/요금은 여기서 만들지 않고 shuttleData.js 값을 그대로 사용한다.
function buildShuttleSchedules(routes = shuttleData.routes ?? []) {
  const schedules = []
  for (const route of routes) {
    const stopMap = ROUTE_STOP_MAP[route.routeId]
    const firstStop = route.stops?.[0]
    if (!firstStop) continue

    const direction = route.routeId.endsWith('_to_school')
      ? 'toCampus'
      : route.routeId.startsWith('school_to_')
        ? 'fromCampus'
        : 'loop'

    ;(firstStop.times ?? []).forEach((time, index) => {
      if (route.routeId === 'gosaek_to_school' && time === '10:00') {
        schedules.push({
          id: 'gosaek_1000_direct',
          routeId: route.routeId,
          routeName: route.routeName,
          direction,
          departureTime: time,
          buses: firstStop.buses?.[index],
          fare: route.fare,
          stopIds: getTripStopIds(route.routeId, time, stopMap?.stopIds ?? [], { variant: 'direct' }),
          boardingStopId: stopMap?.boardingStopId,
          tripVariant: 'direct',
        })
        schedules.push({
          id: 'gosaek_1000_campus_loop',
          routeId: route.routeId,
          routeName: `${route.routeName} 순환`,
          direction,
          departureTime: time,
          buses: firstStop.buses?.[index],
          fare: route.fare,
          stopIds: getTripStopIds(route.routeId, time, stopMap?.stopIds ?? [], { variant: 'campus_loop' }),
          boardingStopId: stopMap?.boardingStopId,
          tripVariant: 'campus_loop',
        })
        return
      }
      schedules.push({
        id: `${route.routeId}_${time.replace(':', '')}`,
        routeId: route.routeId,
        routeName: route.routeName,
        direction,
        departureTime: time,
        buses: firstStop.buses?.[index],
        fare: route.fare,
        stopIds: getTripStopIds(route.routeId, time, stopMap?.stopIds ?? []),
        boardingStopId: stopMap?.boardingStopId,
      })
    })
  }
  return schedules
}

// 시간대별 전체 스케줄 — shuttleData.js에서 파생 생성 (직접 하드코딩하지 않음)
export const SHUTTLE_SCHEDULES = buildShuttleSchedules()
